/**
 * App Bridge 4 Detection Utility
 * 
 * Simple utilities for detecting and working with App Bridge 4
 */

import { GlobalState } from "../core/_Services/GlobalState";

interface ShopifyAppBridge {
  navigate(path: string): void;
  
  // Configuration details injected by Shopify
  config: {
    apiKey: string;
    appOrigins: string[];
    debug: Record<string, any>;
    disabledFeatures: string[];
    experimentalFeatures: string[];
    host: string;
    locale: string;
    shop: string;
  };

  // Environment info
  environment: {
    embedded: boolean; // true when running inside Shopify Admin
    mobile: boolean;
    pos: boolean;
  };

  // App-level info
  origin: string; // Shopify admin origin
  protocol: {
    send: (data: any) => void;
    subscribe: (callback: (data: any) => void) => () => void;
  };

  // Key methods and APIs
  idToken(): Promise<string>; // fetches short-lived session token (JWT, ~1min)
  ready: Promise<any>; // resolves when Shopify App Bridge is ready
  loading: (show: boolean) => Promise<void>; // show/hide loading indicators

  toast: {
    show: (message: string, options?: any) => void;
    hide: (id?: string) => void;
  };

  modal: {
    show: (options: any) => Promise<void>;
    hide: (id?: string) => Promise<void>;
    toggle: (id?: string) => Promise<void>;
  };

  picker: (options: any) => Promise<any>; // open various picker modals
  resourcePicker: (options: any) => Promise<any>; // open resource picker

  saveBar: {
    show: (id: string) => Promise<void>;
    hide: (id: string) => Promise<void>;
    toggle: (id: string) => Promise<void>;
    leaveConfirmation: () => Promise<void>;
  };

  scanner: {
    capture: (options?: any) => Promise<any>;
  };

  scopes: {
    query: () => string[];
    request: (scopes: string[]) => Promise<void>;
    revoke: (scopes: string[]) => Promise<void>;
  };

  sidekick: {
    isOpen: () => boolean;
    open: () => void;
    close: () => void;
    toggle: () => void;
    generate: (options: any) => Promise<any>;
  };

  pos: {
    cart: Record<string, any>;
    close: () => void;
    device: () => Promise<any>;
    location: () => Promise<any>;
  };

  support: {
    registerHandler: (handler: (request: any) => any) => void;
  };

  user: () => Promise<any>; // fetch info about current admin user

  webVitals: {
    onReport: (callback: (metrics: any) => void) => void;
  };

  reviews: {
    request: (options: any) => Promise<any>;
  };

  intents: {
    register: (options: any) => void;
    invoke: (name: string, options?: any) => Promise<any>;
  };

  app: {
    extensions: (options?: any) => any;
  };

  // Internal APIs (use with caution)
  _internal?: {
    modal?: Record<string, any>;
    [key: string]: any;
  };
}

declare global {
  interface Window {
    shopify?: ShopifyAppBridge;
  }
}

/**
 * Check if App Bridge is available (embedded mode)
 * @returns boolean indicating if window.shopify exists and has idToken
 */
export function isAppBridgeAvailable(): boolean {
  return typeof (window as any).shopify !== 'undefined' && typeof (window as any).shopify.idToken === 'function';
}

/**
 * True when this document is a nested Shopify frame (app window / modal src iframe).
 * Contextual save bar and some App Bridge UI APIs only work in the main app iframe.
 */
export function isNestedShopifyAppFrame(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get("bulk_edit_window") === "1") return true;
    if (params.get("bulk_edit_modal") === "1") return true;
  } catch {
    /* ignore */
  }
  try {
    return Boolean(window.opener && window.opener !== window);
  } catch {
    return false;
  }
}

/**
 * Check if app is in embedded mode
 * Checks both App Bridge availability and iframe context
 * @returns boolean indicating if app is embedded
 */
export function isEmbedded(): boolean {
  // If App Bridge is available, we're definitely in embedded mode
  if (isAppBridgeAvailable()) {
    return true;
  }
  
  // Fallback: check if we're in an iframe
  return window.self !== window.top;
}

/**
 * Use for UX that should only appear in the Shopify Admin embedded app
 * (e.g. hide manual product ID / title fields; show resource picker only).
 * Slightly broader than {@link isEmbedded} so first paint matches Shopify embed
 * (URL flag, session embed flag, App Bridge environment).
 */
export function isEmbeddedView(): boolean {
  if (typeof window === "undefined") return false;
  if (isEmbedded()) return true;
  if (window.name === "app-iframe") return true;
  try {
    if (new URLSearchParams(window.location.search).get("embedded") === "1") {
      return true;
    }
  } catch {
    /* ignore */
  }
  try {
    if (GlobalState.get("isEmbed") === "true") return true;
  } catch {
    /* ignore */
  }
  const envEmbedded = (window as any).shopify?.environment?.embedded;
  if (envEmbedded === true) return true;
  return false;
}

/**
 * Wait for App Bridge to be available on window.shopify
 * @param timeoutMs - Maximum time to wait (default: 5000ms)
 * @returns Promise that resolves when App Bridge is ready
 */
export function waitForShopify(timeoutMs: number = 5000): Promise<ShopifyAppBridge> {
  return new Promise((resolve, reject) => {
    // If already available, resolve immediately
    if (window.shopify) {
      resolve(window.shopify);
      return;
    }

    // Set up polling
    const startTime = Date.now();
    const pollInterval = 50;
    
    const poll = () => {
      if (window.shopify) {
        resolve(window.shopify);
        return;
      }
      
      if (Date.now() - startTime > timeoutMs) {
        reject(new Error(`App Bridge not available after ${timeoutMs}ms`));
        return;
      }
      
      setTimeout(poll, pollInterval);
    };
    
    poll();
  });
}

/**
 * Get the current Shopify environment info
 * @returns Object with embedded status and host, or null if not available
 */
export function getShopifyEnvironment(): { embedded: boolean; host: string } | null {
  if (!isAppBridgeAvailable()) {
    return null;
  }
  
  return {
    embedded: (window as any)?.shopify.environment?.embedded ?? isEmbedded(),
    host: (window as any)?.shopify?.config?.host ?? "",
  };
}

/**
 * Return a normalized navigator object for App Bridge, if available.
 * This attempts several common adaptors across different App Bridge integrations
 * so callers can call `navigate(path)` without risking a TypeError.
 */
/** Query keys Shopify Admin adds for embedded apps; keep them on in-app navigations so App Bridge stays valid. */
const SHOPIFY_EMBED_QUERY_KEYS = ["host", "shop", "embedded", "hmac", "session", "timestamp"] as const;

/**
 * Merges Shopify embed params from the current location into a target path.
 * Use when calling React Router `navigate()` in embedded mode so `host` is not dropped from the URL.
 */
export function mergeShopifyEmbedSearchIntoPath(pathnameAndMaybeQuery: string, currentSearch: string): string {
  const current = new URLSearchParams(
    currentSearch.startsWith("?") ? currentSearch.slice(1) : currentSearch,
  );
  const hasEmbed = SHOPIFY_EMBED_QUERY_KEYS.some((k) => current.has(k));
  if (!hasEmbed) return pathnameAndMaybeQuery;

  const qIndex = pathnameAndMaybeQuery.indexOf("?");
  const pathPart = qIndex === -1 ? pathnameAndMaybeQuery : pathnameAndMaybeQuery.slice(0, qIndex);
  const routeQuery = qIndex === -1 ? "" : pathnameAndMaybeQuery.slice(qIndex + 1);

  const merged = new URLSearchParams(routeQuery);
  for (const k of SHOPIFY_EMBED_QUERY_KEYS) {
    if (current.has(k)) merged.set(k, current.get(k)!);
  }
  const q = merged.toString();
  return q ? `${pathPart}?${q}` : pathPart;
}

/** Build a target app route, keep Shopify embed params, and strip bulk-edit query keys. */
export function buildAppRouteExitPath(
  targetPathname: string,
  currentSearch: string,
  stripQueryKeys: string[] = [],
): string {
  const base = mergeShopifyEmbedSearchIntoPath(targetPathname, currentSearch);
  const queryIndex = base.indexOf("?");
  if (queryIndex < 0) return base;
  const params = new URLSearchParams(base.slice(queryIndex + 1));
  stripQueryKeys.forEach((key) => params.delete(key));
  const qs = params.toString();
  return qs ? `${base.slice(0, queryIndex)}?${qs}` : base.slice(0, queryIndex);
}

const POLARIS_WEB_COMPONENT_TAGS = [
  "s-button",
  "s-icon",
  "s-badge",
  "s-link",
  "s-modal",
  "s-app-window",
  "s-table",
  "s-table-cell",
] as const;

/**
 * Wait until core Polaris web components are registered (polaris.js / app-bridge-ui).
 * Standalone local dev loads polaris.js async — tables/modals need this before first paint.
 */
export async function waitForPolarisWebComponents(
  timeoutMs: number = 12000,
): Promise<void> {
  if (typeof customElements === "undefined") {
    return;
  }
  const pending = POLARIS_WEB_COMPONENT_TAGS.filter((tag) => !customElements.get(tag));
  if (pending.length === 0) {
    return;
  }
  await Promise.race([
    Promise.all(pending.map((tag) => customElements.whenDefined(tag))),
    new Promise<void>((_, reject) => {
      setTimeout(() => reject(new Error("Polaris web components not defined")), timeoutMs);
    }),
  ]).catch(() => {
    /* individual pages may still fall back to Polaris React */
  });
}

/**
 * Wait until Polaris <s-modal> web component is registered (app-bridge-ui script loaded).
 */
export async function waitForSModalElement(timeoutMs: number = 8000): Promise<void> {
  if (typeof customElements === "undefined") {
    return;
  }
  if (customElements.get("s-modal")) {
    return;
  }
  await Promise.race([
    customElements.whenDefined("s-modal"),
    new Promise<void>((_, reject) => {
      setTimeout(() => reject(new Error("s-modal not defined")), timeoutMs);
    }),
  ]).catch(() => {
    /* overlay APIs may still be unavailable; caller handles fallback */
  });
}

type ShopifyModalElement = HTMLElement & {
  show?: () => Promise<void> | void;
  hide?: () => Promise<void> | void;
  showOverlay?: () => void;
  hideOverlay?: () => void;
};

/** Show modal via s-modal.show(), App Bridge modal API, or showOverlay(). */
export async function showShopifyModalOverlay(
  modalId: string,
  modalElement: HTMLElement | null,
): Promise<void> {
  const el = modalElement as ShopifyModalElement | null;

  if (typeof el?.show === "function") {
    try {
      await el.show();
      return;
    } catch {
      /* fall through */
    }
  }

  const shopifyModal = (window as any).shopify?.modal;
  if (shopifyModal && typeof shopifyModal.show === "function") {
    try {
      await shopifyModal.show({ id: modalId });
      return;
    } catch {
      try {
        await shopifyModal.show(modalId);
        return;
      } catch {
        /* fall through */
      }
    }
  }

  if (typeof el?.showOverlay === "function") {
    el.showOverlay();
  }
}

/** Hide modal via s-modal.hide(), App Bridge modal API, or hideOverlay(). */
export async function hideShopifyModalOverlay(
  modalId: string,
  modalElement: HTMLElement | null,
): Promise<void> {
  const el = modalElement as ShopifyModalElement | null;

  if (typeof el?.hide === "function") {
    try {
      await el.hide();
      return;
    } catch {
      /* fall through */
    }
  }

  const shopifyModal = (window as any).shopify?.modal;
  if (shopifyModal && typeof shopifyModal.hide === "function") {
    try {
      await shopifyModal.hide(modalId);
      return;
    } catch {
      /* fall through */
    }
  }

  if (typeof el?.hideOverlay === "function") {
    el.hideOverlay();
  }
}

/**
 * Open a URL outside the embedded app scope (billing, OAuth, etc.).
 * Waits for App Bridge, then retries top-frame navigation — native window.open
 * is blocked cross-origin until App Bridge overrides it.
 */
export async function openEmbeddedExternalUrl(
  url: string,
  options?: { maxAttempts?: number; delayMs?: number },
): Promise<void> {
  const maxAttempts = options?.maxAttempts ?? 20;
  const delayMs = options?.delayMs ?? 300;

  try {
    const shopify = await waitForShopify(10000);
    if (shopify?.ready) {
      await shopify.ready;
    }
  } catch {
    /* continue — retries may still succeed once App Bridge override is active */
  }

  let attempts = 0;
  await new Promise<void>((resolve) => {
    const tryOpen = () => {
      window.open(url, "_top");
      attempts += 1;
      if (attempts >= maxAttempts) {
        resolve();
        return;
      }
      setTimeout(tryOpen, delayMs);
    };
    tryOpen();
  });
}

export async function getShopifyNavigator(timeoutMs: number = 1000): Promise<{
  navigate?: (path: string) => void;
} | null> {
  try {
    const shopify = await waitForShopify(timeoutMs);
    if (!shopify) return null;

    // Preferred: direct navigate() method
    if (typeof (shopify as any).navigate === "function") {
      return { navigate: (path: string) => (shopify as any).navigate(path) };
    }

    // Fallback: protocol.send navigation
    if (shopify.protocol && typeof (shopify.protocol as any).send === "function") {
      return { navigate: (path: string) => (shopify.protocol as any).send({ type: "navigate", path }) };
    }

    // Some environments expose a redirect helper
    if (typeof (shopify as any).redirect === "function") {
      return { navigate: (path: string) => (shopify as any).redirect(path) };
    }

    return null;
  } catch (err) {
    return null;
  }
}
